import React, {useLayoutEffect, useMemo} from 'react'
import TableProvider, {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import {
  useCollectionAdd, useCollectionDeleteBulk, useCollectionClone,
  useCollectionDelete,
  useCollectionGet,
  useCollectionList, useCollectionMerge
} from 'services/collection/collection.hooks';
import constants from 'helpers/constants';
import {useEffectEvent, useEffectItem, useProviderView} from 'helpers/hooks/utils';
import {
  useCollectionColumns, useCollectionFilterGroups,
  useCollectionPatch, useCollectionProfileCallbacks, useCollectionSelected,
  useCollectionToggleFavorite, useCollectionToggleLabel,
  useCollectionToggleVisibility
} from 'services/collection/collection.utils';
import {useParams} from 'react-router-dom';
import {useClientGet} from 'services/client/client.hooks';
import {useAuthClientId} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import useMediaQuery from '@mui/material/useMediaQuery';

const CollectionTableLoader = ({children, active}) => {
  const tableProvider = useTable();
  const listState = useEffectItem(tableProvider.appliedListState());

  const list = useCollectionList({
    search: listState.search,
    filter: listState.filter,
    sort: listState.sort,
    page: listState.pagination.pageIndex,
    pageSize: listState.pagination.pageSize
  }, {
    ...constants.queryOptions.infinite,
    enablePreload: true,
    enabled: (active && listState.active)
  });

  const setListEvent = useEffectEvent(tableProvider.setList);
  useLayoutEffect(() => {
    setListEvent?.(list);
  }, [setListEvent, list]);

  return <React.Fragment>
    {children}
  </React.Fragment>
}

const CollectionsPreLoader = ({active, id}) => {
  useCollectionGet({
    collectionId: id
  },
  {enabled: (active && +id > 0)});
}

const CollectionsTableProvider = (props) => {
  const params = useParams();
  const { collectionId } = {...params, ...props};

  const clientId = useAuthClientId();

  const dataKey = 'collectionId';

  const client = useClientGet({clientId}, {enabled: clientId >= 0});
  const collection = useCollectionGet({collectionId}, {enabled: +collectionId > 0});

  const view = useProviderView('collection', null, props.skipView);

  const profileCallbacks = useCollectionProfileCallbacks();
  const columns = useCollectionColumns(view);
  const filterGroups = useCollectionFilterGroups(view);

  // isLoading anything, is loading something definition related
  const isLoading = props?.isLoading || client.status?.isLoading;
  const isDefinitionsLoading = props?.isDefinitionsLoading;

  const columnDefinitions = useMemo(() => {
    return columns?.length > 0 ? columns : null;
  }, [columns]);

  const fieldData = useMemo(() => ({
    callbacks: profileCallbacks
  }), [profileCallbacks]);

  const filterGroupDefinitions = useMemo(() => {
    return filterGroups?.length > 0 ? filterGroups : null;
  }, [filterGroups]);

  const smDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const options = useMemo(() => ({
    listState: {
      initial: {
        pageSize: 25
      },
      options: {
        type: constants.appState.type.local,
        name: 'collections',
        searchParams: true,
        searchParamsRoutes: /^((?!\/entities).)*$/i // apply searchParams only before entities
      }
    },
    columnState: {
      initial: {},
      options: {
        name: 'collections',
        type: constants.appState.type.local
      }
    },
    graphState: {
      initial: {},
      options: {
        name: 'collections',
        type: constants.appState.type.local
      }
    },
    assistantState: {
      initial: {},
      options: {
        name: 'collections',
        type: constants.appState.type.local
      }
    },
    columnDefinitions: !smDown ? columnDefinitions :
      (columnDefinitions || [])
        .map((columnDef) => ({
          ...columnDef,
          visible: ['name', 'actions'].includes(columnDef.name),
          pinned: false
        })),
    filterGroupDefinitions
  }), [columnDefinitions, filterGroupDefinitions, smDown]);

  const handleCompare = (itmA, itmB) => {
    return +itmA.collectionId === +itmB.collectionId;
  };

  const updateData = useCollectionPatch();
  const createData = useCollectionAdd();
  const cloneData = useCollectionClone();
  const mergeData = useCollectionMerge();
  const deleteData = useCollectionDelete();
  const deleteDataBulk = useCollectionDeleteBulk();
  const toggleLabel = useCollectionToggleLabel();
  const toggleFavorite = useCollectionToggleFavorite({refetchContext: false});
  const toggleVisibility = useCollectionToggleVisibility({refetchContext: false});
  const selectedItems = useCollectionSelected();
  const updaters = useMemo(() => {
    return {
      updateData: updateData,
      createData: (data) => {
        return createData.mutation.mutateAsync(data);
      },
      cloneData: (data) => {
        return cloneData.mutation.mutateAsync(data);
      },
      mergeData: (data) => {
        return mergeData.mutation.mutateAsync(data);
      },
      deleteData: (data) => {
        return deleteData.mutation.mutateAsync(data);
      },
      deleteDataBulk: (data) => {
        return deleteDataBulk.mutation.mutateAsync(data);
      },
      toggleLabel: toggleLabel,
      toggleFavorite: toggleFavorite,
      toggleVisibility: toggleVisibility,
      selectedItems: selectedItems
    }
  }, [createData.mutation, cloneData.mutation, mergeData.mutation, updateData, deleteData.mutation, selectedItems,
    deleteDataBulk.mutation, toggleLabel, toggleFavorite, toggleVisibility]);

  return <TableProvider {...props}
                        view={view}
                        dataKey={dataKey}
                        data={collection}
                        context={client}
                        options={options}
                        fieldData={fieldData}
                        updaters={updaters}
                        LoaderComponent={CollectionTableLoader}
                        PreLoaderComponent={CollectionsPreLoader}
                        isDefinitionsLoading={isDefinitionsLoading}
                        isLoading={isLoading}
                        onCompare={handleCompare}>
    {props.children}
  </TableProvider>
};

CollectionsTableProvider.propTypes = {
}

export default CollectionsTableProvider;
